"use client"

import { useState, useEffect } from "react"
import { getAuth } from "firebase/auth"
import { getDatabase, ref as dbRef, push, onValue } from "firebase/database"
import "./RaiseIssuePage.css"
import { useLocation } from "react-router-dom"
import { FaInfoCircle } from "react-icons/fa"
import { db, ref, get, set } from "../Firebase/firebase-config"
import { useGetCurrentUserId } from "../hooks/useGetCurrentUserId"

const RaiseIssuePage = () => {
  const location = useLocation()
  const orderDetails = location.state || {}
  const { userId } = useGetCurrentUserId()

  const [issueType, setIssueType] = useState("")
  const [description, setDescription] = useState("")
  const [priority, setPriority] = useState("")
  const [attachment, setAttachment] = useState(null)
  const [contactInfo, setContactInfo] = useState({ name: "", email: "" })
  const [confirmation, setConfirmation] = useState(false)
  const [issuesHistory, setIssuesHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [companyName, setCompanyName] = useState("")
  const [siteAddress, setSiteAddress] = useState(orderDetails.siteAddress || "")
  const [brand, setBrand] = useState(orderDetails.brand || "")
  const [grade, setGrade] = useState(orderDetails.grade || "")
  const [dispatchDetails, setDispatchDetails] = useState(orderDetails.dispatchDetails || "")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)
  const [showIssues, setShowIssues] = useState(false)

  useEffect(() => {
    const auth = getAuth()
    const user = auth.currentUser
    if (user) {
      setContactInfo({
        name: user.username || "",
        email: user.email || "",
      })
    }
  }, [])

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) {
        setLoading(false)
        return
      }

      try {
        const profileRef = ref(db, `profileDashboard/${userId}`)
        const snapshot = await get(profileRef)
        if (snapshot.exists()) {
          const profileData = snapshot.val()
          setCompanyName(profileData.company_name || "Unknown Company")
        } else {
          setError("Company name not found in profileDashboard.")
        }
      } catch (err) {
        console.error("Error fetching user data:", err)
        setError("Error fetching company information")
      }
    }
    fetchUserData()
  }, [userId])

  useEffect(() => {
    if (orderDetails) {
      setBrand(orderDetails.brand || "")
      setGrade(orderDetails.grade || "")
      setSiteAddress(orderDetails.siteAddress || "")
      setDispatchDetails(orderDetails.dispatchDetails || "")
    }
  }, [orderDetails])

  useEffect(() => {
    const fetchIssueHistory = () => {
      if (!userId) {
        setLoading(false)
        return
      }

      const database = getDatabase()
      const issuesRef = dbRef(database, `issues/${userId}`)
      const unsubscribe = onValue(issuesRef, (snapshot) => {
        const data = snapshot.val()
        const issues = data ? Object.values(data) : []
        setIssuesHistory(issues)
        setLoading(false)
      })

      return unsubscribe
    }

    const unsub = fetchIssueHistory()
    return () => {
      if (unsub) unsub()
    }
  }, [userId])

  const getIndianTimestamp = () => {
    const now = new Date()
    const istOffset = 5.5 * 60 * 60 * 1000
    const istTime = new Date(now.getTime() + istOffset)
    return istTime.toISOString().replace("T", " ").replace("Z", "")
  }

  const sendNotification = async (issuesId, companyName) => {
    if (!userId) return

    const notificationRef = ref(db, `supplier_notifications/Raised Issues/${userId}/${issuesId}`)
    const gifUrl = "Images/New notification.gif"

    const newNotification = {
      title: "Issues",
      brand,
      siteAddress,
      issueType,
      message: `A new issue has been sent by ${companyName}. Please review the issue.`,
      gifUrl,
      timestamp: getIndianTimestamp(),
      seen: false,
    }

    try {
      await set(notificationRef, newNotification)
    } catch (error) {
      console.error("Error sending notification:", error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const cleaned = description.replace(/\s/g, "")
    if (cleaned.length < 10 || cleaned.length > 100) {
      setError("Description must be between 10 and 100 characters.")
      return
    }

    if (!userId) {
      setError("User not authenticated")
      return
    }

    if (issueType && description && brand && grade) {
      const database = getDatabase()
      const issuesRef = dbRef(database, `issues/${userId}`)
      const newIssue = {
        issueType,
        description,
        brand,
        companyName,
        grade,
        date: new Date().toLocaleString("en-In", { timeZone: "Asia/Kolkata" }),
        status: "Pending",
        contactInfo,
        siteAddress,
        dispatchDetails,
      }
      try {
        const newIssueRef = await push(issuesRef, newIssue)
        const issuesId = newIssueRef.key

        if (issuesId) {
          await sendNotification(issuesId, companyName)
        }

        setConfirmation(true)
        setIssueType("")
        setDescription("")
        setBrand("")
        setGrade("")
        setSiteAddress("")
        setDispatchDetails("")
        setAttachment(null)

        setSuccess(true)
        setTimeout(() => {
          setSuccess(false)
          setConfirmation(false)
        }, 2000)
      } catch (error) {
        console.error("Error submitting issue:", error)
        setError("Error submitting issue")
      }
    }
  }

  const toggleIssuesHistory = () => {
    setShowIssues((prev) => !prev)
  }

  const issueInfo = () => {
    alert(
      "This page allows you to submit various types of issues, such as technical problems or billing concerns, etc. You can also view your issues response in previous Issues.",
    )
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
      </div>
    )
  }

  return (
    <div className="container-fluid raise-issue-container">
      <div className="issue-form">
        <h1 className="text-center">
          Raise an Issue{" "}
          <FaInfoCircle onClick={issueInfo} style={{ fontSize: "18px", color: "#EA8716", cursor: "pointer" }} />
        </h1>

        <form onSubmit={handleSubmit}>
          <div className="issue-group">
            <label className="issue-label">Brand</label>
            <select className="form-control" value={brand} onChange={(e) => setBrand(e.target.value)} required>
              <option value="">Select Brand</option>
              <option value="Ultratech">Ultratech</option>
              <option value="Shree">Shree</option>
              <option value="JSW">JSW</option>
              <option value="ACC">ACC</option>
              <option value="JKSuper">JK Super</option>
              <option value="Ambuja">Ambuja</option>
            </select>
          </div>

          <div className="issue-group">
            <label className="issue-label">Grade</label>
            <select className="form-control" value={grade} onChange={(e) => setGrade(e.target.value)} required>
              <option value="">Select Grade</option>
              <option value="OPC43">OPC43</option>
              <option value="OPC53">OPC53</option>
              <option value="PSC">PSC</option>
              <option value="PPC">PPC</option>
            </select>
          </div>

          <div className="issue-group">
            <label className="issue-label">Site Address</label>
            <input
              type="text"
              className="form-control"
              value={siteAddress}
              onChange={(e) => setSiteAddress(e.target.value)}
              minLength={5}
              maxLength={150}
              required
            />
          </div>

          <div className="issue-group">
            <label className="issue-label">Dispatch Details</label>
            <input
              type="text"
              className="form-control"
              value={dispatchDetails}
              readOnly
              style={{ backgroundColor: "#f8f9fa" }}
            />
          </div>

          <div className="issue-group">
            <label className="issue-label">Issue Type</label>
            <select className="form-control" value={issueType} onChange={(e) => setIssueType(e.target.value)} required>
              <option value="">Select Issue Type</option>
              <option value="Technical">Technical Issue</option>
              <option value="Order/Delivery">Order/Delivery Issue</option>
              <option value="Payment/Billing">Payment/Billing Issue</option>
              <option value="Suggestion">Suggestion</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="issue-group">
            <label className="issue-label">Description</label>
            <textarea
              className="form-control"
              rows="4"
              placeholder="Enter description here under (10-100 characters)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              minLength={10}
              maxLength={100}
              required
            />
            <small>{description.replace(/\s/g, "").length}/100 characters</small>
          </div>

          {error && (
            <div className="error-message" style={{ color: "red", marginBottom: "10px" }}>
              {error}
            </div>
          )}
          {success && <div className="success-Message text-white">Issue submitted successfully!</div>}

          <button type="submit" className="btn-submit">
            Submit Issue
          </button>
        </form>
      </div>

      <div className="issue-history">
        <h2>Previous Issues</h2>
        {issuesHistory.length > 0 ? (
          issuesHistory.map((issue, index) => (
            <div className="issue-card" key={index}>
              <h3>{issue.issueType}</h3>
              <p>
                <strong>Brand:</strong> {issue.brand}
              </p>
              <p>
                <strong>Grade:</strong> {issue.grade}
              </p>
              <p>
                <strong>Date:</strong> {issue.date}
              </p>
              <p className={`status ${issue.status === "Pending" ? "" : "resolved"}`}>{issue.status}</p>
            </div>
          ))
        ) : (
          <p>No issues found.</p>
        )}
      </div>
    </div>
  )
}

export default RaiseIssuePage
